import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const studentFeatures = [
  { label: '本课摘要', icon: 'ri-article-line', color: 'primary' as const },
  { label: '核心概念', icon: 'ri-lightbulb-line', color: 'accent' as const },
  { label: '问题链', icon: 'ri-question-answer-line', color: 'primary' as const },
  { label: '案例链', icon: 'ri-stack-line', color: 'accent' as const },
  { label: '重点视频片段', icon: 'ri-play-circle-line', color: 'primary' as const },
  { label: '课后测验', icon: 'ri-edit-2-line', color: 'accent' as const },
  { label: '常见疑问', icon: 'ri-chat-3-line', color: 'primary' as const },
];

export default function P63ServiceExpert({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />服务学生
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            把评课结果转化为<span className="text-gold-600">智能学习支持</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            评课过程中识别出的重点和证据，可以继续成为学生的课堂回看与复习资源
          </p>
        </div>

        {/* Student App Features */}
        <div className={`max-w-4xl mx-auto mb-4 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="p-6 md:p-8 glass-liquid rounded-2xl">
            <div className="relative z-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {studentFeatures.map((f, i) => (
                <div
                  key={f.label}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl glass border border-background-200/40 text-center"
                  style={{ transitionDelay: `${300 + i * 80}ms` }}
                >
                  <div className={`w-11 h-11 rounded-xl ${f.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center`}>
                    <i className={`${f.icon} text-lg ${f.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                  </div>
                  <span className="text-xs font-body font-semibold text-foreground-800">{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Video replay highlight */}
        <div className={`max-w-lg mx-auto mb-4 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-3 p-4 rounded-xl glass-gold border border-gold-200/40">
            <div className="w-10 h-10 rounded-lg bg-gold-100/80 border border-gold-300/40 flex items-center justify-center">
              <i className="ri-links-line text-lg text-gold-600" />
            </div>
            <div>
              <p className="text-sm font-body font-bold text-gold-800">点击知识点 → 跳转到对应视频时间</p>
              <p className="text-xs text-gold-600 font-body">不用重新拖动完整视频寻找内容</p>
            </div>
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              学生的疑问、回看位置和课后反馈还能反向汇总，帮助教师判断哪些内容已经讲清、哪些概念仍然存在理解障碍。<strong className="text-gold-700">课堂评价与学生学习不再是两条分离链路</strong>，而是形成双向连接。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}